import { AssetType } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import { UIActionType, isUIActionRestricted } from "js/core/uiActionsRestrictors";

export enum FileSource {
    Local = "local",
    Google = "google",
    OneDrive = "onedrive",
    Dropbox = "dropbox",
    Box = "box",
    Bynder = "bynder",
    Frontify = "frontify",
    Vimeo = "vimeo",
    Youtube = "youtube"
}

export enum FileType {
    Image = "image",
    Video = "video",
    Spreadsheet = "spreadsheet",
    Document = "document",
    Presentation = "presentation"
}

export const allowedExtentionsPerFileType = {
    [FileType.Image]: ["jpg", "jpeg", "png", "gif", "svg", "svg+xml"],
    [FileType.Video]: ["mp4", "webm", "mov", "avi", "wmv", "flv", "quicktime", "json"],
    [FileType.Spreadsheet]: [
        "xls", "xlsx", "csv",
        "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ],
    [FileType.Document]: [
        "doc", "docx", "txt", "pdf", "text/plain",
        "application/pdf", "application/msword",
        "application/vnd.google-apps.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ],
    [FileType.Presentation]: [
        "ppt", "pptx", "pps", "pot", "potx", "ppsx", "pptm", "pptx", "ppsm", "ppsx", "potm", "potx",
        "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ]
};

export const maxFileSizePerFileType = {
    [FileType.Image]: {
        size: 1024 * 1024 * 50,
        label: "50mb"
    },
    [FileType.Video]: {
        size: 1024 * 1024 * 500,
        label: "500mb"
    }
};

export const aiContextFileTypes = [FileType.Document, FileType.Presentation, FileType.Spreadsheet];

export function getAllowedFileSources(fileType: FileType): FileSource[] {
    const canUseIntegrations = WorkspaceController.actionPermissions[ActionPermissionsObject.INTEGRATIONS].use;

    const fileSources: FileSource[] = [];
    const fileTypeIsMedia = [FileType.Image, FileType.Video].includes(fileType);
    const fileTypeIsVideo = FileType.Video === fileType;

    if (canUseIntegrations) {
        fileSources.push(FileSource.Google, FileSource.OneDrive, FileSource.Dropbox, FileSource.Box);
        if (fileTypeIsMedia) {
            fileSources.push(FileSource.Bynder, FileSource.Frontify);
        }
    }
    if (!isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.VIDEO, source: "hosted" }) && fileTypeIsVideo) {
        fileSources.push(FileSource.Vimeo);
        fileSources.push(FileSource.Youtube);
    }

    return fileSources;
}

export function fileTypesToExtensions(fileTypes: FileType[]) {
    const extensions = [];
    for (const fileType of fileTypes) {
        extensions.push(...allowedExtentionsPerFileType[fileType]);
    }
    return extensions;
}

export function validateFileTypeAndSize(file: File, allowedFileTypes: FileType[] = []) {
    const mimeTypes = file.type.split("/");
    const fileType = mimeTypes[0] as FileType;
    const fileExtension = mimeTypes[1];

    const allowedFileExtensions = fileTypesToExtensions(allowedFileTypes);
    if (allowedFileExtensions && !allowedFileExtensions.some(ext => ext.includes(fileExtension) || ext.includes(file.type))) {
        return `Uploaded ${fileType} extension ${fileExtension} is not supported`;
    }

    if (maxFileSizePerFileType[fileType]) {
        if (file.size > maxFileSizePerFileType[fileType].size) {
            return `Uploaded ${fileType} should have max size ${maxFileSizePerFileType[fileType].label}`;
        }
    }
}
